import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, SpinnerGap, PaperPlaneTilt, Fire,
  ArrowsClockwise, CheckCircle, Lightning, Timer
} from "@phosphor-icons/react";
import { useLanguage, usePageTitle } from "./i18n";
import { VoiceRecordButton, TTSPlayButton } from "./VoiceFeatures";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBubble = ({ message, isOracle, user }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`flex ${isOracle ? 'justify-start' : 'justify-end'} mb-4`}
  >
    <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
      isOracle
        ? 'bg-[var(--card-bg)] border border-[var(--glass-border)] text-foreground'
        : 'bg-amber-500/15 border border-amber-500/30 text-foreground'
    }`}>
      {isOracle && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <Fire size={14} weight="fill" className="text-amber-500" />
          <span className="text-[10px] font-heading tracking-[0.2em] uppercase text-amber-500/80">ONIMA</span>
        </div>
      )}
      {message.content}
      {isOracle && user && (
        <div className="mt-2 flex justify-end">
          <TTSPlayButton text={message.content} user={user} />
        </div>
      )}
    </div>
  </motion.div>
);

const AskezaCard = ({ suggestion, onAccept, onAnother, language, accepting }) => {
  const { t } = useLanguage();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mx-auto max-w-md my-4"
      data-testid="askeza-ai-suggestion-card"
    >
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/30 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Fire size={20} weight="fill" className="text-amber-500" />
          <span className="font-heading tracking-[0.15em] uppercase text-xs text-amber-400">
            {t('askezaChat.suggestedPractice')}
          </span>
        </div>
        
        <h3 className="text-foreground font-medium text-base mb-2">{suggestion.name}</h3>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <Timer size={14} />
            {suggestion.duration} {t('askezaChat.days')}
          </span>
          <span className="flex items-center gap-1">
            <Lightning size={14} />
            {suggestion.type}
          </span>
        </div>
        
        {suggestion.goal && (
          <p className="text-sm text-muted-foreground mb-4 italic">
            {t('askezaChat.goal')}: {suggestion.goal}
          </p>
        )}
        
        <div className="flex gap-2">
          <button
            onClick={onAccept}
            disabled={accepting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl 
                       bg-amber-500/20 border border-amber-500/40 text-amber-400
                       hover:bg-amber-500/30 hover:border-amber-500/60
                       transition-all text-sm font-heading tracking-wider
                       disabled:opacity-50"
            data-testid="askeza-ai-accept-btn"
          >
            {accepting ? (
              <SpinnerGap size={16} className="animate-spin" />
            ) : (
              <CheckCircle size={16} weight="fill" />
            )}
            {t('askezaChat.accept')}
          </button>
          <button
            onClick={onAnother}
            disabled={accepting}
            className="px-4 py-2.5 rounded-xl 
                       bg-[var(--overlay-bg)] border border-[var(--glass-border)]
                       text-muted-foreground hover:text-foreground
                       hover:border-starlight/30 transition-all text-sm"
            data-testid="askeza-ai-another-btn"
          >
            <ArrowsClockwise size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const AskezaChat = ({ user, onBack, onUserUpdate, onShowAuth }) => {
  const { t, language } = useLanguage();
  usePageTitle('askeza');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [quickReplies, setQuickReplies] = useState([]);
  const [askezaSuggestion, setAskezaSuggestion] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionInitializedRef = useRef(false);
  const chatInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Store user ID at mount to prevent re-init on user object changes
  const initialUserIdRef = useRef(user?.id);

  // Start session - only once
  useEffect(() => {
    if (sessionInitializedRef.current && sessionId) {
      return;
    }

    const startSession = async () => {
      try {
        const res = await axios.post(`${API}/askeza/ai/start`, {
          user_id: initialUserIdRef.current || null,
          language,
        });
        setSessionId(res.data.session_id);
        setMessages([{ role: 'oracle', content: res.data.initial_message }]);
        sessionInitializedRef.current = true;
      } catch (e) {
        void 0;
      } finally {
        setIsStarting(false);
      }
    };
    startSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]); // Removed user?.id - causes re-init on user update

  const sendMessage = async (text) => {
    if (!text?.trim() || isLoading || !sessionId) return;
    
    const messageText = text.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setIsLoading(true);
    setQuickReplies([]);
    setAskezaSuggestion(null);

    try {
      const res = await axios.post(`${API}/askeza/ai/chat`, {
        session_id: sessionId,
        message: messageText,
        user_id: user?.id || null,
        language,
      });

      setMessages(prev => [...prev, { role: 'oracle', content: res.data.response }]);

      if (res.data.quick_replies?.length > 0) {
        setQuickReplies(res.data.quick_replies);
      }
      if (res.data.askeza_suggestion) {
        setAskezaSuggestion(res.data.askeza_suggestion);
      }
      if (res.data.user && onUserUpdate) {
        onUserUpdate(res.data.user);
      }
    } catch (e) {
      if (e.response?.status === 402) {
        setMessages(prev => [...prev, { role: 'oracle', content: t('chat.outOfCredits') }]);
      } else {
        setMessages(prev => [...prev, { role: 'oracle', content: t('chat.connectionLost') }]);
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => chatInputRef.current?.focus(), 50);
    }
  };

  const handleAcceptAskeza = async () => {
    if (!user) {
      onShowAuth?.();
      return;
    }
    if (!askezaSuggestion || accepting) return;
    
    setAccepting(true);
    try {
      await axios.post(`${API}/askeza/ai/accept`, {
        user_id: user.id,
        session_id: sessionId,
        askeza_type: askezaSuggestion.type || 'other',
        name: askezaSuggestion.name,
        goal: askezaSuggestion.goal || '',
        duration: askezaSuggestion.duration || 21,
        enable_reminders: false,
      });
      
      setAskezaSuggestion(null);
      setAccepted(true);
      
      const confirmMsgs = {
        ru: `Аскеза «${askezaSuggestion.name}» принята! Она начинается завтра и продлится ${askezaSuggestion.duration} дней. Ты можешь следить за ней в своём профиле.`,
        en: `Askeza "${askezaSuggestion.name}" accepted! It starts tomorrow and will last ${askezaSuggestion.duration} days. Track it in your profile.`,
        uk: `Аскеза «${askezaSuggestion.name}» прийнята! Вона починається завтра і триватиме ${askezaSuggestion.duration} днів. Слідкуй за нею у профілі.`,
        de: `Askese "${askezaSuggestion.name}" angenommen! Sie beginnt morgen und dauert ${askezaSuggestion.duration} Tage. Verfolge sie in deinem Profil.`,
      };
      setMessages(prev => [...prev, { role: 'oracle', content: confirmMsgs[language] || confirmMsgs.ru }]);
    } catch (e) {
      void 0;
    } finally {
      setAccepting(false);
    }
  };

  const handleSuggestAnother = () => {
    setAskezaSuggestion(null);
    const msgs = {
      ru: "Предложи другую аскезу",
      en: "Suggest another practice",
      uk: "Запропонуй іншу аскезу",
      de: "Schlage eine andere Askese vor",
    };
    sendMessage(msgs[language] || msgs.ru);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  return (
    <div className="flex-1 bg-background flex flex-col relative overflow-hidden" data-testid="askeza-chat-page">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-b border-[var(--glass-border)] px-6 py-4"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="askeza-chat-back-btn"
          >
            <ArrowLeft size={20} />
            <span className="font-heading text-starlight tracking-[0.15em] text-xs uppercase">ONIMA</span>
          </button>
          <div className="flex items-center gap-2">
            <Fire size={22} weight="duotone" className="text-amber-500" />
            <span className="font-heading text-amber-400 tracking-widest uppercase text-sm">
              {t('askezaChat.title')}
            </span>
          </div>
          <div className="w-16 text-right">
            {user && <span className="text-xs text-starlight/60">{user.total_credits}</span>}
          </div>
        </div>
      </motion.header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-6 py-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          {isStarting ? (
            <div className="flex justify-center py-20">
              <SpinnerGap size={48} className="text-amber-500 animate-spin" />
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <ChatBubble key={i} message={msg} isOracle={msg.role === 'oracle'} user={user} />
              ))}

              {/* Askeza suggestion card */}
              <AnimatePresence>
                {askezaSuggestion && !isLoading && (
                  <AskezaCard
                    suggestion={askezaSuggestion}
                    onAccept={handleAcceptAskeza}
                    onAnother={handleSuggestAnother}
                    language={language}
                    accepting={accepting}
                  />
                )}
              </AnimatePresence>

              {/* Accepted confirmation */}
              {accepted && !askezaSuggestion && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center my-4"
                >
                  <button
                    onClick={onBack}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl
                               bg-amber-500/15 border border-amber-500/30 text-amber-400
                               hover:bg-amber-500/25 transition-all text-sm font-heading tracking-wider"
                    data-testid="askeza-chat-go-profile-btn"
                  >
                    <CheckCircle size={18} weight="fill" />
                    {t('askezaChat.goToProfile')}
                  </button>
                </motion.div>
              )}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 text-muted-foreground mb-4"
                >
                  <SpinnerGap size={20} className="animate-spin" />
                  <span className="text-sm italic">{t('chat.oracleThinking')}</span>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </main>

      {/* Quick replies + Input */}
      <div className="relative z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-t border-[var(--glass-border)] px-6 py-4">
        {quickReplies.length > 0 && !isLoading && !askezaSuggestion && (
          <div className="max-w-3xl mx-auto mb-3">
            <div className="flex flex-wrap gap-2 justify-center">
              {quickReplies.map((reply, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(reply)}
                  className="px-3 py-1.5 rounded-full text-xs
                             bg-[var(--overlay-bg)] border border-[var(--glass-border)]
                             text-muted-foreground hover:text-foreground hover:border-amber-500/30
                             transition-all"
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto flex gap-3 items-center">
          {user && (
            <VoiceRecordButton
              user={user}
              onTranscription={(text) => sendMessage(text)}
              disabled={isLoading}
            />
          )}
          <input
            ref={chatInputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('askezaChat.inputPlaceholder')}
            className="flex-1 bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3
                       text-foreground placeholder:text-muted-foreground
                       focus:border-starlight/60 focus:outline-none text-sm transition-colors"
            disabled={isLoading}
            autoFocus
            data-testid="askeza-chat-input"
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="px-4 py-3 rounded-xl bg-amber-500/20 border border-amber-500/30
                       text-amber-400 hover:bg-amber-500/30
                       transition-all disabled:opacity-30"
            data-testid="askeza-chat-send-btn"
          >
            <PaperPlaneTilt size={20} weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskezaChat;
